import React, { useCallback, useMemo, useState } from 'react';
import { BarChart3, Target, Users } from 'lucide-react';
import type { DeptUnit } from '../../../services/executiveDeptUnitsService';
import { TeamCapacityKpiCards } from './TeamCapacityKpiCards';
import { TeamCapacityOperatorTable } from './TeamCapacityOperatorTable';
import { useTeamCapacityData } from './useTeamCapacityData';
import type { KpiFilter, SortDirection, TeamSortColumn } from './teamCapacityShared';
import { isCritical, sortMembersByColumn } from './teamCapacityShared';
import { EXEC_SECTION_LABEL } from '../executive/opsExecutiveShared';

interface DeptCapacitySidePanelProps {
  organizationId: string;
  unit: DeptUnit;
  periodLabel: string;
  initialUserId?: string | null;
}

function InitialsAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white">
      {initials}
    </span>
  );
}

export const DeptCapacitySidePanel: React.FC<DeptCapacitySidePanelProps> = ({
  organizationId,
  unit,
  periodLabel,
  initialUserId = null,
}) => {
  const memberUserIds = useMemo(
    () => unit.members.map((m) => m.user_id),
    [unit.members],
  );

  const { items, members, kpis, isLoading } = useTeamCapacityData(organizationId, {
    memberUserIds: memberUserIds.length > 0 ? memberUserIds : undefined,
    departmentId: unit.department.id,
  });

  const [kpiFilter, setKpiFilter] = useState<KpiFilter>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(initialUserId);
  const [sortColumn, setSortColumn] = useState<TeamSortColumn>('bottleneck');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSortChange = useCallback((col: TeamSortColumn) => {
    setSortColumn((prevCol) => {
      if (prevCol === col) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setSortDirection(col === 'name' ? 'asc' : 'desc');
      return col;
    });
  }, []);

  const displayMembers = useMemo(() => {
    let filtered = members;
    if (kpiFilter === 'overdue') filtered = filtered.filter((m) => m.tasks_overdue > 0);
    else if (kpiFilter === 'critical') filtered = filtered.filter(isCritical);
    return sortMembersByColumn(filtered, sortColumn, sortDirection);
  }, [members, kpiFilter, sortColumn, sortDirection]);

  const managerName = unit.manager?.user_name ?? unit.manager?.user_email?.split('@')[0] ?? null;
  const deptColor = unit.department.color ?? '#64748b';

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-[11px] text-[var(--text-muted)] animate-pulse">
        Carregando detalhes do setor…
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden"
      style={{ borderTopWidth: 3, borderTopColor: deptColor }}
    >
      <header className="shrink-0 border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/50 px-4 py-2.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className={EXEC_SECTION_LABEL}>Detalhe do Setor</p>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: deptColor }} />
              <h2 className="truncate text-[15px] font-bold text-[var(--text-primary)]">{unit.department.name}</h2>
            </div>
          </div>
          <span className="text-[9px] text-[var(--text-muted)]">{periodLabel}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-[var(--text-secondary)]">
          {managerName && (
            <span className="inline-flex items-center gap-1.5">
              <InitialsAvatar name={managerName} />
              Gestor: <span className="font-medium text-[var(--text-primary)]">{managerName}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Users size={11} />
            {unit.members.length} operador{unit.members.length !== 1 ? 'es' : ''}
          </span>
          <span className="inline-flex items-center gap-1">
            <Target size={11} />
            {unit.projects?.length ?? 0} projeto{(unit.projects?.length ?? 0) !== 1 ? 's' : ''}
          </span>
          {(unit.actionCount ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1 font-medium text-amber-600">
              <BarChart3 size={11} />
              {unit.actionCount} ação{(unit.actionCount ?? 0) !== 1 ? 'ões' : ''}
              {(unit.overdueCount ?? 0) > 0 && (
                <span className="text-red-500">({unit.overdueCount} atras.)</span>
              )}
            </span>
          )}
        </div>
      </header>

      <div className="shrink-0 border-b border-[var(--border-default)]/50">
        <TeamCapacityKpiCards
          kpis={kpis}
          members={members}
          activeFilter={kpiFilter}
          onFilterClick={setKpiFilter}
          compact
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-1 pb-1 pt-1">
        <p className="mb-1 shrink-0 px-2 text-[9px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
          Operadores · Ocupação · SLA · Gargalos
        </p>
        <div className="min-h-0 flex-1 overflow-hidden">
          <TeamCapacityOperatorTable
            members={displayMembers}
            items={items}
            selectedUserId={selectedUserId}
            onSelectUser={setSelectedUserId}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            maxHeight="h-full"
          />
        </div>
      </div>
    </div>
  );
};

export default DeptCapacitySidePanel;
